import {
  Functions,
  httpsCallable,
  type HttpsCallableOptions,
} from 'firebase/functions'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useFunctions } from 'reactfire'
import * as Sentry from '@sentry/react'
import { appVersion } from './general'

export async function api(
  functions: Functions,
  method: string,
  data = {},
  // Per-call callable options. Most callers rely on the SDK default (~70s
  // client timeout); long-running methods (e.g. bulk zip) can raise `timeout`
  // to match the server's limit so the client doesn't give up early. CL-1784.
  options?: HttpsCallableOptions
) {
  try {
    const result = await httpsCallable(
      functions,
      'api',
      options
    )({ method, appVersion, ...data })
    return result.data
  } catch (err) {
    // Bare callable errors (e.g. `FirebaseError: INTERNAL` from a 500) have
    // no stacktrace and no hint of which dispatcher method was called.
    // Enrich Sentry context so the next event is debuggable. CL-1318.
    Sentry.addBreadcrumb({
      category: 'api.error',
      level: 'error',
      message: `api/${method}`,
      data: { method },
    })
    if (err && typeof err === 'object') {
      try {
        ;(err as Record<string, unknown>).apiMethod = method
      } catch {
        // Some thrown values are frozen — fall back to the breadcrumb alone.
      }
    }
    throw err
  }
}

export type ApiQueryStatus = 'idle' | 'loading' | 'error' | 'success'

export interface ApiQueryResult<ResponseData> {
  data: ResponseData
  status: ApiQueryStatus
  error: Error | undefined
}

// Fetch-driven replacement for reactfire's `useCallableFunctionResponse` (our
// former `useApiResponse` wrapper), matching its shape
// (`data` / `status` / `error`) and its placeholder contract: `data` is the exact
// `startWithValue` reference while a (re)fetch is in flight or after a failure, so
// callers that compare `data === startWithValue` (their `isPlaceholder`) still
// hold their last-good value through the gap.
//
// The difference — and the whole point — is that this does NOT carry reactfire's
// built-in 30s cache-reset timer (`SuspenseSubject`'s `_timeoutWindow`), which was
// silently re-firing every Chatwoot inbox hook (directory, labels, counts,
// messages, per-company fan-out…) every ~30s per idle operator regardless of app
// state, and was the dominant source of operator-inbox request volume / 503s
// (CL-1790). Here a refetch happens ONLY when the request payload changes — i.e.
// when the caller intends it (a `refreshKey` bump from a realtime event or the
// backstop poll, a filter/company change, a manual reload key). Idle tabs go
// quiet.
export function useApiQuery<RequestData, ResponseData>(
  method: string,
  options: {
    data?: RequestData
    startWithValue: ResponseData
    // When false, no request is made: the status is `idle` and `data` keeps
    // whatever the last completed fetch returned (the placeholder if there has
    // been none). Lets callers skip provably-empty fetches (e.g. messages with
    // no conversation selected) without discarding a loaded result.
    enabled?: boolean
    httpsCallableOptions?: HttpsCallableOptions
  }
): ApiQueryResult<ResponseData> {
  const { data, startWithValue, enabled = true, httpsCallableOptions } = options
  const functions = useFunctions()

  // Serialize the request so a changed payload triggers a refetch, mirroring how
  // reactfire keyed its cached observable on the callable name + data.
  const requestKey = useMemo(
    () => JSON.stringify({ method, data: data ?? {}, enabled }),
    [method, data, enabled]
  )

  // Latest values captured in refs so they can be read inside the effect without
  // being effect deps: `startWithValue` / `httpsCallableOptions` are frequently
  // fresh object references each render, and `data` is already covered by
  // `requestKey` (which encodes its serialized contents).
  const startWithValueRef = useRef(startWithValue)
  startWithValueRef.current = startWithValue
  const httpsCallableOptionsRef = useRef(httpsCallableOptions)
  httpsCallableOptionsRef.current = httpsCallableOptions
  const dataRef = useRef(data)
  dataRef.current = data

  type TrackedState = ApiQueryResult<ResponseData> & { requestKey: string }

  const [state, setState] = useState<TrackedState>(() => ({
    data: startWithValue,
    status: enabled ? 'loading' : 'idle',
    error: undefined,
    requestKey,
  }))

  useEffect(() => {
    if (!enabled) {
      setState((previous) =>
        previous.status === 'idle' && previous.requestKey === requestKey
          ? previous
          : {
              data: previous.data,
              status: 'idle',
              error: undefined,
              requestKey,
            }
      )
      return
    }

    let cancelled = false
    setState({
      data: startWithValueRef.current,
      status: 'loading',
      error: undefined,
      requestKey,
    })

    api(
      functions,
      method,
      (dataRef.current ?? {}) as Record<string, unknown>,
      httpsCallableOptionsRef.current
    )
      .then((response) => {
        if (cancelled) {
          return
        }
        setState({
          data: response as ResponseData,
          status: 'success',
          error: undefined,
          requestKey,
        })
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        setState({
          data: startWithValueRef.current,
          status: 'error',
          error: err instanceof Error ? err : new Error(String(err)),
          requestKey,
        })
      })

    return () => {
      cancelled = true
    }
    // requestKey encodes method + data + enabled — the intended sole trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functions, requestKey])

  const staleForRequestKey = state.requestKey !== requestKey

  if (!enabled) {
    return { data: state.data, status: 'idle', error: undefined }
  }

  return {
    data: staleForRequestKey ? startWithValueRef.current : state.data,
    status: staleForRequestKey ? 'loading' : state.status,
    error: staleForRequestKey ? undefined : state.error,
  }
}
